import React, { useState } from "react";
import { Input, Modal, Select, message } from "antd";

import { controller as rootController } from "../../../../controller";

/**
 * "Something here is wrong" — raised from CarrierContactBar in the insurance modal.
 *
 * The people who discover a carrier's number is stale are the FTC staff dialling
 * it, so this is open to anyone who can see the bar. It does NOT edit the carrier:
 * it files a suggestion for a super-admin to accept in the CRM's Requests page.
 * The backend validates the payload against an allow-list, so nothing here can
 * reach a field that isn't shown below.
 *
 * Prefilled with the current values so the reviewer gets a real diff rather than a
 * free-text note, and so the submitter only changes what's actually wrong.
 */

const METHOD_OPTIONS = [
  { value: "", label: "— not set —" },
  { value: "portal", label: "On portal" },
  { value: "call", label: "By phone" },
  { value: "call_email", label: "By phone & email" },
];

// Must match crm.PlatformRequest.ALLOWED_FIELDS[CARRIER_CONTACT]. The server
// rejects anything else outright rather than dropping it silently.
const FIELDS = [
  { key: "phone_number", label: "Phone number", placeholder: "(800) 555-0100" },
  { key: "portal_name", label: "Portal name", placeholder: "e.g. Provider Connect" },
  { key: "portal_url", label: "Portal URL", placeholder: "https://…" },
];

export default function CarrierContactSuggestModal({ open, onClose, policy }) {
  const p = policy || {};
  const current = {
    phone_number: p.carrier_phone_number || "",
    portal_name: p.carrier_portal_name || "",
    portal_url: p.carrier_portal_url || "",
    breakdown_method: p.carrier_breakdown_method || "",
    estimate_method: p.carrier_estimate_method || "",
  };

  const [values, setValues] = useState(current);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset on every OPEN, not on carrier change. The modal isn't destroyed on close,
  // so keying the reset off the carrier meant closing and reopening for the SAME
  // carrier kept whatever half-finished edit had been abandoned — and then sent it.
  const carrierId = policy && policy.carrier_id;
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setValues(current);
      setNote("");
    }
  }

  function set(key, value) {
    setValues(function (prev) { return Object.assign({}, prev, { [key]: value }); });
  }

  // Only what actually changed — a payload of untouched values would read as a
  // proposed edit to every field and give the reviewer nothing to judge.
  const changed = Object.keys(values).filter(function (k) {
    return (values[k] || "") !== (current[k] || "");
  });

  async function handleSubmit() {
    if (!carrierId) {
      message.error("This policy has no carrier on file to correct.");
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      changed.forEach(function (k) { payload[k] = values[k]; });
      const res = await rootController.submitPlatformRequest({
        request_type: "carrier_contact",
        target_id: carrierId,
        payload: payload,
        note: note,
      });
      if (res && res.status >= 200 && res.status < 300) {
        message.success("Thanks — sent for review.");
        onClose();
        return;
      }
      message.error(((res && res.json) || {}).detail || "Couldn't send the suggestion.");
    } catch (e) {
      console.error(e);
      message.error("Couldn't send the suggestion.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Suggest a correction"
      okText={"Send" + (changed.length ? ` (${changed.length})` : "")}
      onOk={handleSubmit}
      okButtonProps={{ loading: saving, disabled: changed.length === 0 }}
      destroyOnClose={false}
    >
      <p style={{ color: "#848696", fontSize: 13, marginTop: -4 }}>
        This doesn't change anything straight away — it goes to the Smilepass team
        to review. Only edit what's wrong.
      </p>

      {FIELDS.map(function (f) {
        return (
          <div key={f.key} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12.5, color: "#6f6c80", marginBottom: 4 }}>{f.label}</div>
            <Input
              value={values[f.key]}
              placeholder={f.placeholder}
              onChange={function (e) { set(f.key, e.target.value); }}
            />
          </div>
        );
      })}

      {[
        { key: "breakdown_method", label: "How we get breakdowns" },
        { key: "estimate_method", label: "How we get estimates" },
      ].map(function (f) {
        return (
          <div key={f.key} style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12.5, color: "#6f6c80", marginBottom: 4 }}>{f.label}</div>
            <Select
              style={{ width: "100%" }}
              value={values[f.key]}
              options={METHOD_OPTIONS}
              onChange={function (v) { set(f.key, v); }}
              getPopupContainer={function (t) { return t.parentElement; }}
            />
          </div>
        );
      })}

      <div style={{ marginTop: 4 }}>
        <div style={{ fontSize: 12.5, color: "#6f6c80", marginBottom: 4 }}>
          Anything the reviewer should know (optional)
        </div>
        <Input.TextArea
          rows={2}
          value={note}
          placeholder="e.g. the old number now goes to a disconnected line"
          onChange={function (e) { setNote(e.target.value); }}
        />
      </div>
    </Modal>
  );
}
